import { ArrowRight, Truck, Shield, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useCMSContent } from "@/hooks/useCMSContent";
import { HeroAdvertisement } from "@/components/HeroAdvertisement";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import heroImageDefault from "@/assets/hero-marketplace.jpg";

const HeroSection = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { settings } = useSiteSettings();
  const { content: cmsHero } = useCMSContent('homepage_hero');
  const [satisfiedCustomersCount, setSatisfiedCustomersCount] = useState(0);

  // Fetch satisfied customers count
  useEffect(() => {
    const fetchSatisfiedCustomers = async () => {
      try {
        const { data, error } = await supabase.rpc('get_satisfied_customers_count');
        if (error) throw error;
        setSatisfiedCustomersCount(data || 0);
      } catch (error) {
        console.error('Error fetching satisfied customers count:', error);
        setSatisfiedCustomersCount(0);
      }
    };

    fetchSatisfiedCustomers();

    // Subscribe to real-time updates when reviews change
    const reviewsChannel = supabase
      .channel('reviews_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'product_reviews' }, () => {
        fetchSatisfiedCustomers();
      })
      .subscribe();

    // Subscribe to settings changes
    const settingsChannel = supabase
      .channel('settings_satisfied_customers')
      .on('postgres_changes', { 
        event: 'UPDATE', 
        schema: 'public', 
        table: 'site_settings',
        filter: 'key=eq.satisfied_customers_count'
      }, () => {
        fetchSatisfiedCustomers();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(reviewsChannel);
      supabase.removeChannel(settingsChannel);
    };
  }, []);

  const cmsHeroUrl = (cmsHero?.featured_image as string) || (cmsHero?.content?.hero_image_url as string) || '';
  const rawSettingUrl = typeof settings.hero_image_url === 'string' ? settings.hero_image_url.trim() : '';
  const pickUrl = (url: string) => url && (/^https?:\/\//.test(url) || url.startsWith('/') || url.startsWith('data:'));
  const heroImage = pickUrl(cmsHeroUrl) ? cmsHeroUrl : pickUrl(rawSettingUrl) ? rawSettingUrl : heroImageDefault;

  // Localized site texts with proper fallbacks
  const siteName = settings.site_name || 'Nazandoo';
  const siteTagline = language === 'fr'
    ? (settings.site_tagline || t('site.tagline') || t('hero.title.location'))
    : (t('site.tagline') || t('hero.title.location'));
  const siteDescription = language === 'fr'
    ? (settings.site_description || t('site.description') || t('hero.description'))
    : (t('site.description') || t('hero.description'));

  const handleExplorerProduits = () => {
    const productsSection = document.getElementById('products-section');
    if (productsSection) {
      productsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleDevenirVendeur = () => {
    navigate('/become-vendor');
  };

  return (
    <section className="relative overflow-hidden">
      <HeroAdvertisement />
      <div className="bg-gradient-hero py-20">
        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Hero Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
                {siteName}
                <span className="block text-secondary mt-2">
                  {siteTagline}
                </span>
              </h1>
              <p className="text-xl text-white/90 max-w-lg">
                {siteDescription}
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" variant="secondary" className="group" onClick={handleExplorerProduits}>
                {t('hero.explore_products')}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20" onClick={handleDevenirVendeur}>
                {t('hero.become_vendor')}
              </Button>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="bg-white/10 border-white/20 backdrop-blur">
                <CardContent className="p-4 text-center">
                  <Truck className="h-8 w-8 text-secondary mx-auto mb-2" />
                  <h3 className="font-semibold text-white">{t('hero.fast_delivery')}</h3>
                  <p className="text-sm text-white/80">{t('hero.fast_delivery.desc')}</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/10 border-white/20 backdrop-blur">
                <CardContent className="p-4 text-center">
                  <Shield className="h-8 w-8 text-secondary mx-auto mb-2" />
                  <h3 className="font-semibold text-white">{t('hero.secure_payment')}</h3>
                  <p className="text-sm text-white/80">{t('hero.secure_payment.desc')}</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/10 border-white/20 backdrop-blur">
                <CardContent className="p-4 text-center">
                  <Zap className="h-8 w-8 text-secondary mx-auto mb-2" />
                  <h3 className="font-semibold text-white">{t('hero.easy_pickup')}</h3>
                  <p className="text-sm text-white/80">{t('hero.easy_pickup.desc')}</p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative">
            <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={heroImage}
                alt={settings.site_name || 'Marketplace Kinshasa'}
                className="w-full h-full object-cover bg-gradient-to-br from-primary/10 to-secondary/10"
              />
            </div>
            {/* Floating notification */}
            <div className="absolute -bottom-4 -left-4 bg-white rounded-lg shadow-elegant p-4 max-w-xs">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-success rounded-full flex items-center justify-center">
                  <Shield className="h-6 w-6 text-success-foreground" />
                </div>
                <div>
                  <p className="font-semibold text-sm">{t('hero.secure_order')}</p>
                  <p className="text-xs text-muted-foreground">
                    +{satisfiedCustomersCount} {t('hero.clients_satisfied_label')}
                  </p>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
        
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent"></div>
      </div>
    </section>
  );
};

export default HeroSection;